/**
 * Pages Module
 *
 * API methods for public pages.
 */

import { get } from '../core/client';
import { getConfig } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import type { SDKResponse, Page } from '../types';

interface PagesResponse {
  pages: Page[];
}

interface PageResponse {
  page: Page;
}

const PAGES_CACHE_TTL = 5 * 60 * 1000;

/**
 * Get all public pages for the store
 */
export async function getPages(): Promise<SDKResponse<Page[]>> {
  const config = getConfig();
  const response = await get<PagesResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug/pages', {
      storeSlug: config.storeSlug,
    }),
    {
      cache: {
        key: `pages:${config.storeSlug}`,
        ttl: PAGES_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data.pages,
    };
  }

  return {
    success: false,
    message: response.message,
  };
}

/**
 * Get a public page by slug
 */
export async function getPage(slug: string): Promise<SDKResponse<Page>> {
  const config = getConfig();
  const response = await get<PageResponse>(
    buildEndpoint('/v1/storefront/shops/name/:storeSlug/pages/:slug', {
      storeSlug: config.storeSlug,
      slug,
    }),
    {
      cache: {
        key: `page:${config.storeSlug}:${slug}`,
        ttl: PAGES_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data.page,
    };
  }

  return {
    success: false,
    message: response.message,
  };
}
